import { Highlight } from '@components/highlight/highlight';
import { useLiteStorefront } from '@hooks/use-lite-storefront';
import { useAppSelector } from '@redux/hooks';
import { selectViewport } from '@redux/viewport/selectors';
import RfqModal from '@tkmp/rfq/components/RfqModal/RfqModal';
import { useInlineRfqForm } from '@tkmp/rfq/hooks/useInlineRfqForm/useInlineRfqForm';
import { generateDefaultField } from '@tkmp/rfq/utils';
import { Link } from '@tkww/orion-web-ds-action';
import classNames from 'classnames';
import React from 'react';
import Styles from './location-highlights.scss';

export type LocationHighlightsProps = {
	serviceArea?: string;
	travelsUpTo: number | null;
	travelRange: string | null;
	isAddressPublic: boolean;
};

const highlightTitle = ({
	isMobileAndPaid,
	travelsUpTo,
	travelRange,
}: {
	isMobileAndPaid: boolean;
	travelsUpTo: number | null;
	travelRange: string | null;
}) => {
	if (travelRange) {
		return `Travel area: ${travelRange}`;
	}

	return isMobileAndPaid
		? `Up to ${travelsUpTo} miles`
		: `Travels up to ${travelsUpTo} miles`;
};

export const LocationHighlights: React.FC<LocationHighlightsProps> = ({
	serviceArea,
	travelsUpTo,
	travelRange,
	isAddressPublic,
}) => {
	const isMobile = useAppSelector((state) => selectViewport(state)).isMobile;
	const isLiteStorefront = useLiteStorefront();

	const localFormContext = useInlineRfqForm({
		freeTextLabel: '',
		headerText: 'Get a personalized quote',
		textAreaInitialValueOverride: generateDefaultField('textarea'),
		initiator: 'Travel CTA',
	});

	const showServiceAreaHighlight = !!(serviceArea && isAddressPublic);
	const showTravelHighlight = !!(travelsUpTo || travelRange);
	const showDoubleColumn = showServiceAreaHighlight && showTravelHighlight;

	return (
		<div
			className={classNames(
				Styles.container,
				showDoubleColumn ? Styles.double : Styles.single,
			)}
		>
			<RfqModal context={localFormContext} />
			{serviceArea && isAddressPublic && (
				<Highlight
					iconName="pin"
					iconSize={!isLiteStorefront ? 'lg' : 'md'}
					classes={{
						container: Styles.highlight,
						title: Styles.highlightTitle,
						text: Styles.highlightText,
					}}
					highlightTitle={`${serviceArea}`}
					highlightText={
						!isMobile && !isLiteStorefront
							? 'Typically included in the starting cost'
							: 'Service area'
					}
				/>
			)}
			{(travelsUpTo || travelRange) && (
				<Highlight
					iconName="plane"
					iconSize={!isLiteStorefront ? 'lg' : 'md'}
					classes={{
						container: Styles.highlight,
						title: Styles.highlightTitle,
						text: Styles.highlightText,
					}}
					highlightTitle={highlightTitle({
						isMobileAndPaid: isMobile && !isLiteStorefront,
						travelsUpTo,
						travelRange,
					})}
					highlightText={
						<div>
							{isMobile && !isLiteStorefront ? (
								!travelRange && 'Travel range'
							) : (
								<>
									Additional charges may apply
									<Link
										as="button"
										type="button"
										// @ts-expect-error not MouseEvent
										onClick={localFormContext.openModal}
										className={Styles.highlightButton}
										size="md"
										styleType="tertiary"
										data-testid="location-highlights-personalized-quote"
									>
										Get a personalized quote
									</Link>
								</>
							)}
						</div>
					}
				/>
			)}
		</div>
	);
};
